// 2_components/slovnik_pojmu.js
// Wraps glossary phrases in a product description with dashed-underline tooltips.
// Fetches entries from CDN JSON. Only VIP (groupId 3) and admin-logged users see them.

import { byShop, getShopLang } from "../0_global/shop_map";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "SVG", "H1", "H2", "H3", "H4"]);
const VIP_GROUP_ID = 999; // 3
const MAX_PER_WIDGET = 3;
const MIN_DISTANCE = 80;
const CACHE_HOURS = 24;
const GLOSSARY_URLS = {
	cs: "https://cdn.myshoptet.com/usr/www.natios.cz/user/documents/upload/shoptet_uploads/explanation_tooltips.json",
	sk: "https://cdn.myshoptet.com/usr/www.natios.sk/user/documents/upload/shoptet_uploads/explanation_tooltips.json",
};

export function applyGlossary(selector) {
	if (!isVipCustomer()) {
		return;
	}

	const root = typeof selector === "string" ? document.querySelector(selector) : selector;
	if (!root || root.dataset.slovnikApplied) {
		return;
	}

	loadGlossary().then(function (entries) {
		if (!entries?.length) {
			return;
		}
		highlightGlossary(root, entries);
		root.dataset.slovnikApplied = "1";
		bindGlossaryInteractions(root);
	});
}

function loadGlossary() {
	const url = byShop(GLOSSARY_URLS);
	const lang = getShopLang();
	const storageKey = "explanation_tooltips_" + lang;
	const expiryKey = storageKey + "_expiry";
	const now = Date.now();
	const expiry = localStorage.getItem(expiryKey);
	const cached = localStorage.getItem(storageKey);

	if (expiry && cached && now < parseInt(expiry, 10)) {
		try {
			return Promise.resolve(normalizeEntries(JSON.parse(cached)));
		} catch (error) {
			localStorage.removeItem(storageKey);
			localStorage.removeItem(expiryKey);
		}
	}

	return fetch(url)
		.then(function (response) {
			if (!response.ok) {
				throw new Error("Glossary fetch failed");
			}
			return response.json();
		})
		.then(function (data) {
			localStorage.setItem(storageKey, JSON.stringify(data));
			localStorage.setItem(expiryKey, String(now + CACHE_HOURS * 60 * 60 * 1000));
			return normalizeEntries(data);
		})
		.catch(function (error) {
			console.warn("[NAT] slovnik_pojmu.js: failed to load glossary", error);
			return [];
		});
}

function normalizeEntries(data) {
	const list = Array.isArray(data) ? data : data?.entries;
	if (!Array.isArray(list)) {
		return [];
	}
	return list.filter(function (entry) {
		return entry?.word && entry?.explanation;
	});
}

function isVipCustomer() {
	if (document.body.classList.contains("admin-logged")) {
		return true;
	}
	const customer = typeof getShoptetDataLayer === "function" ? getShoptetDataLayer("customer") : null;
	return Number(customer?.groupId) === VIP_GROUP_ID;
}

function getExplanation(entry) {
	return entry.explanationVip || entry.explanation;
}

function highlightGlossary(root, entries) {
	const lookup = new Map();
	for (const entry of entries) {
		lookup.set(String(entry.word).toLowerCase(), entry);
		for (const form of entry.forms || []) {
			if (form) {
				lookup.set(String(form).toLowerCase(), entry);
			}
		}
	}

	const allForms = [...lookup.keys()].filter(Boolean);
	if (!allForms.length) {
		return;
	}

	allForms.sort(function (a, b) {
		return b.length - a.length;
	});
	const escaped = allForms.map(function (p) {
		return p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	});
	const regex = new RegExp("(?<!\\p{L})(" + escaped.join("|") + ")(?!\\p{L})", "giu");
	const used = collectUsedEntries(root, lookup);
	const widgets = root.querySelectorAll(".product-widget");
	const targets = widgets.length ? widgets : [root];

	targets.forEach(function (target) {
		replaceTextInNode(target, regex, lookup, used, {
			count: 0,
			sinceLast: MIN_DISTANCE,
		});
	});
}

function markEntryUsed(used, entry) {
	if (!entry) {
		return;
	}
	used.add(entry);
	used.add(String(entry.word).toLowerCase());
	(entry.forms || []).forEach(function (form) {
		if (form) {
			used.add(String(form).toLowerCase());
		}
	});
}

function isEntryUsed(used, entry, phrase) {
	if (!entry) {
		return true;
	}
	return used.has(entry) || used.has(phrase.toLowerCase()) || used.has(String(entry.word).toLowerCase());
}

function collectUsedEntries(root, lookup) {
	const used = new Set();
	root.querySelectorAll(".slovnik-tooltip").forEach(function (el) {
		const word = (el.dataset.slovnikWord || el.childNodes[0]?.textContent || "").trim().toLowerCase();
		if (!word) {
			return;
		}
		markEntryUsed(used, lookup.get(word));
		used.add(word);
	});
	return used;
}

function createTooltip(phrase, entry) {
	const tooltip = document.createElement("span");
	tooltip.className = "slovnik-tooltip";
	tooltip.dataset.slovnikWord = String(entry.word).toLowerCase();
	if (entry.id) {
		tooltip.dataset.slovnikId = entry.id;
	}
	tooltip.appendChild(document.createTextNode(phrase));

	const bubble = document.createElement("span");
	bubble.className = "slovnik-tooltip__bubble";
	bubble.setAttribute("role", "tooltip");
	const text = document.createElement("span");
	text.className = "slovnik-tooltip__text";
	text.textContent = getExplanation(entry);
	bubble.appendChild(text);
	tooltip.appendChild(bubble);
	return tooltip;
}

function replaceTextInNode(node, regex, lookup, used, state) {
	if (node.nodeType === Node.TEXT_NODE) {
		const text = node.textContent;
		if (!regex.test(text)) {
			regex.lastIndex = 0;
			state.sinceLast += text.length;
			return;
		}
		regex.lastIndex = 0;

		const fragment = document.createDocumentFragment();
		let lastIndex = 0;
		let match;

		while ((match = regex.exec(text)) !== null) {
			const phrase = match[0];
			const entry = lookup.get(phrase.toLowerCase());
			const gap = match.index - lastIndex;

			if (isEntryUsed(used, entry, phrase)) {
				continue;
			}

			const canWrap = state.count < MAX_PER_WIDGET && state.sinceLast + gap >= MIN_DISTANCE;
			if (!canWrap) {
				continue;
			}

			if (match.index > lastIndex) {
				fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
			}

			markEntryUsed(used, entry);
			state.count += 1;
			state.sinceLast = 0;
			fragment.appendChild(createTooltip(phrase, entry));
			lastIndex = regex.lastIndex;
		}

		if (lastIndex === 0) {
			state.sinceLast += text.length;
			return;
		}

		if (lastIndex < text.length) {
			fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
			state.sinceLast += text.length - lastIndex;
		}

		node.parentNode.replaceChild(fragment, node);
		return;
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return;
	}
	if (SKIP_TAGS.has(node.nodeName) || node.classList.contains("slovnik-tooltip")) {
		return;
	}

	Array.from(node.childNodes).forEach(function (child) {
		replaceTextInNode(child, regex, lookup, used, state);
	});
}

function positionGlossaryTooltip(e) {
	const el = e.currentTarget;
	const rect = el.getBoundingClientRect();
	const tooltipWidth = Math.min(280, document.body.clientWidth - 16);
	const margin = 8;
	const bodyWidth = document.body.clientWidth;
	const center = rect.left + rect.width / 2;
	const tooltipLeft = center - tooltipWidth / 2;
	const tooltipRight = center + tooltipWidth / 2;

	if (tooltipLeft < margin) {
		const shift = margin - rect.left;
		el.style.setProperty("--slovnik-tooltip-left", shift + "px");
		el.style.setProperty("--slovnik-tooltip-transform", "none");
		el.style.setProperty("--slovnik-arrow-left", rect.width / 2 - shift + "px");
	} else if (tooltipRight > bodyWidth - margin) {
		const shift = bodyWidth - margin - rect.left - tooltipWidth;
		el.style.setProperty("--slovnik-tooltip-left", shift + "px");
		el.style.setProperty("--slovnik-tooltip-transform", "none");
		el.style.setProperty("--slovnik-arrow-left", rect.width / 2 - shift + "px");
	} else {
		el.style.removeProperty("--slovnik-tooltip-left");
		el.style.removeProperty("--slovnik-tooltip-transform");
		el.style.removeProperty("--slovnik-arrow-left");
	}
}

function bindGlossaryInteractions(root) {
	root.querySelectorAll(".slovnik-tooltip").forEach(function (el) {
		if (el.dataset.slovnikBound) {
			return;
		}
		el.dataset.slovnikBound = "1";
		el.setAttribute("tabindex", "0");
		el.addEventListener("mouseenter", positionGlossaryTooltip);
		el.addEventListener("focus", positionGlossaryTooltip);
		el.addEventListener("click", function (e) {
			if (!window.NAT?.media?.isSmallTablet) {
				return;
			}
			e.preventDefault();
			e.stopPropagation();
			const wasOpen = this.classList.contains("is-open");
			document.querySelectorAll(".slovnik-tooltip.is-open").forEach(function (openEl) {
				openEl.classList.remove("is-open");
			});
			if (!wasOpen) {
				this.classList.add("is-open");
			}
		});
	});

	if (document.body.dataset.slovnikOutsideBound) {
		return;
	}
	document.body.dataset.slovnikOutsideBound = "1";
	document.addEventListener("click", function (e) {
		if (e.target.closest(".slovnik-tooltip")) {
			return;
		}
		document.querySelectorAll(".slovnik-tooltip.is-open").forEach(function (el) {
			el.classList.remove("is-open");
		});
	});
}
